import React, { useEffect, useState } from "react";
import { Button } from "../../../../Components/ui/button";
import { Input } from "../../../../Components/ui/input";
import { Label } from "../../../../Components/ui/label";
import { Card } from "../../../../Components/ui/card";
import { Textarea } from "../../../../Components/ui/textarea";
import { Smile } from "lucide-react";
import { FontBoldIcon, FontItalicIcon, CodeIcon } from "@radix-ui/react-icons";
import { Strikethrough } from "lucide-react";
import { AlertTriangle, Info } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { useToast } from "../../../../Components/ui/use-toast";
import { Toaster } from "../../../../Components/ui/toaster";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "../../../../Components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../Components/ui/select";
import { useCampaignContext } from "../CampaignContext";
import { getSmsPartsCount } from "../utils/getSmsPartsCount";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../Components/ui/hover-card';
import { useCampaignValidators } from "../hooks/useCampaignValidators";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/src/State/store";

const textAreaIcon = () => (
  <div className="relative w-8 h-8 flex justify-center items-center">
    <div
      className="absolute rounded w-[3px] h-8 transform rotate-45"
      style={{ borderLeft: "3px solid #64748B" }}
    />
    <div
      className="absolute w-[3px] h-4 rounded transform rotate-45 translate-x-2 translate-y-2"
      style={{ borderLeft: "3px solid #64748B" }}
    />
  </div>
);

interface Props {
  onClose: () => void;
}

const CreateContentSMS: React.FC<Props> = ({ onClose }) => {
  const [templateName, setTemplateName] = useState("");
  const [language, setLanguage] = useState("en");
  const [showPicker, setShowPicker] = useState(false);
  const { setSmsContent, partsCount, setPartsCount, campaignName, setBody, body, setShowWarning, showWarning, setIsSmsContentSubmitAttempted, smsBodyError, isSmsContentSubmitAttempted, resetSMSContent , smsContent , isViewMode , isUpdateMode } = useCampaignContext();
  const { validateSmsBody } = useCampaignValidators();
  const toast = useToast();
  const workspaceId = useSelector((state: RootState) => state.authentication.workspace_id);



  const applyFormatting = (symbol: string) => {
    const textarea = document.querySelector("textarea[name='bodyText']") as HTMLTextAreaElement;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.slice(start, end);
    const formatted = `${symbol}${selectedText}${symbol}`;
    setBody(textarea.value.slice(0, start) + formatted + textarea.value.slice(end));
  };


  const formatText = (text: string) => {
    return text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<s>$1</s>")
      .replace(/`(.*?)`/g, "<code>$1</code>");
  };

  // No content logic
  const noContentSelected = !body.trim();

  const logSmsPartsCount = (text: string) => {
    const smsInfo = getSmsPartsCount(text);
    setPartsCount(smsInfo.parts);
    setShowWarning(smsInfo.parts > 1);
  };

  const handleSMSTemplateSubmit = () => {


    const isBodyValid = validateSmsBody(body);



    const isFormValid = isBodyValid;

    if (!isFormValid) {
      toast.toast({
        title: "Validation Failed",
        description: "Please correct the highlighted errors before saving.",
      });
      return;
    }


    // const templateName = `${campaignName.toLocaleLowerCase()}_sms_${workspaceId}`; // Constructed from context
    const templateName = `${campaignName.toLocaleLowerCase().replace(/\s+/g, '')}_sms_${workspaceId}`;

    const finalSmsPayload = {
      template:{
        channel_id: 2,
        channel_type: "SMS",
        part_count: partsCount, // from context
        campaign_name: campaignName,
        template_content: {
          name: templateName,
          message: body // body text from state
        }
      }

    };

    // ✅ Save to context
    setSmsContent(finalSmsPayload);
    onClose();
  };

  return (
    <div>
      {/* Main layout: left = form, right = preview */}
      <div className="flex flex-col md:flex-row gap-6 mb-6 mt-4">
        {/* Left: Template form */}
        <div className="space-y-6 w-full max-w-[650px] md:w-3/5">
          <Card className="border p-4 rounded-lg text-left h-auto">
            <h2 className="text-base font-semibold mb-2">Content</h2>

            <div className="space-y-4 mt-6">
              <div>
                <Label htmlFor="bodyText">Body</Label>
                {showWarning && (
                  <Card className="flex items-center justify-between border text-black-100 rounded-md px-4 py-4 mt-4 text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span>This content will consume {partsCount} SMS messages.</span>
                    </div>

                    {/* Wrap the Info icon with HoverCard */}
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <span className="cursor-pointer">
                          <Info
                            className="w-4 h-4 cursor-pointer hover:opacity-80"
                            style={{ color: "#3A85F7" }}
                          />

                        </span>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80 p-4 text-sm bg-white shadow-lg border border-gray-200 rounded-xl z-50">
                        <p className="font-semibold text-gray-600 mb-2">Why is the message split?</p>
                        <p className="text-gray-500 mb-2">
                          Most messages use a standard encoding called <strong>GSM-7</strong>, which allows up to <strong>160 characters</strong> in one SMS.
                        </p>

                        <p className="text-gray-500 mb-2">
                          If your message includes special symbols, emojis, or characters from non-English languages, it switches to a different encoding called <strong>UCS-2</strong>, which allows only <strong>70 characters</strong> per SMS.
                        </p>

                        <p className="text-gray-500">
                          When your message goes beyond these limits, it is automatically split into multiple parts.
                        </p>
                      </HoverCardContent>
                    </HoverCard>
                  </Card>
                )}
                <div className="relative mt-4 ">
                  <div className="text-xs text-gray-500 mt-1 flex justify-end mb-2">Character Count: {body.length}</div>
                  <textarea
                    name="bodyText"
                    className="w-full h-[350px] border rounded p-2 text-[#020617] resize-none overflow-y-auto overflow-x-hidden break-all"
                    value={body}
                    placeholder="Hello"
                    onChange={(e) => {
                      const text = e.target.value;
                      setBody(text);
                      logSmsPartsCount(text);
                      validateSmsBody(text); // ✅ Call validator on change
                    }}
                    disabled = {isViewMode}
                  />


                  {isSmsContentSubmitAttempted && smsBodyError && (
                    <p className="text-red-500 text-xs font-medium mt-1 font-sans italic ml-1 text-left">
                      {smsBodyError}
                    </p>)}
                </div>
              </div>
            </div>
          </Card>



        </div>

        {/* Right: SMS Preview */}
        <div className="w-full max-w-[500px]  md:w-2/5 border rounded-lg p-4 min-h-[590px] h-[90vh] flex flex-col">
          <h2 className="mb-2 mt-0 font-bold text-left">Content preview</h2>
          <div
            className="rounded-2xl p-4 h-full bg-[#F0F4F8] overflow-y-auto"
          >

            {noContentSelected ? (
              <div className="flex flex-col items-center justify-center h-full w-full text-center">
                {textAreaIcon()}
                <div className="mt-6">
                  <p className="text-xl font-semibold">Mobile screen</p>
                </div>
                <div className="w-[125px] mt-2" style={{ fontWeight: 500, fontSize: "14px" }}>
                  {/* <p>Preview varies based on platform selection</p> */}
                </div>
              </div>
            ) : (
              <div className="w-full rounded p-2 mt-4 min-h-fit">
                <div
                  className="whitespace-pre-wrap break-words text-sm text-[#020617]"
                  dangerouslySetInnerHTML={{ __html: formatText(body) }}
                />
              </div>

            )}
          </div>
        </div>
      </div>

      {/* Buttons at the right bottom */}
      <div className="flex justify-end space-x-3 mt-6">
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              className="w-[80px] border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              disabled = {isViewMode || isUpdateMode}
            >
              Discard
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogDescription>
                Are you sure you want to discard this Template?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" className="w-24">
                  Cancel
                </Button>
              </DialogClose>

              <Button
                className="w-24"
                onClick={() => {
                  resetSMSContent();
                  onClose();
                }}
              >
                OK
              </Button>

            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Button
          className="w-[100px] bg-[#3A85F7] text-white hover:bg-blue-600"
          onClick={() => {
            setIsSmsContentSubmitAttempted(true); // ✅ Mark submit attempt
            handleSMSTemplateSubmit();            // ✅ Call your submit logic
          }}
          disabled = {isViewMode}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default CreateContentSMS;
